import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { COLORS, TAX_CATEGORIES } from './config/theme';
import {
  subscribeToJobs, createJob, updateJob,
  subscribeToReceipts, createReceipt, updateReceipt, splitReceipt, deleteReceipt,
  subscribeToTimeEntries, createTimeEntriesBatch,
  subscribeToInvoices, createInvoice, updateInvoiceStatus, getNextInvoiceNumber,
  computeJobTotals, seedTaxCategories,
} from './services/firestore';
import { initGoogleDrive, requestDriveAccess, hasDriveAccess, uploadReceiptFile } from './services/googleDrive';
import { extractReceiptData, isAIConfigured, getAIStatus } from './services/receiptAI';
import { compressForAI } from './utils/imageCompress';
import { generateInvoicePDF } from './utils/invoicePDF';
import { fmt, fmtDate, fmtShort, todayISO, weekStartISO } from './utils/format';
import {
  Icon, GlowCard, Button, Input, Select, Badge, StatusBadge,
  Stat, Modal, Toast, PageHeader, Sidebar, Spinner,
} from './components/ui';

// ============================================================
// LOGIN SCREEN
// ============================================================
function LoginScreen() {
  const { login, createUser } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [displayName, setDisplayName] = useState('');

  async function handleSubmit() {
    setError('');
    setLoading(true);
    try {
      if (isCreating) {
        await createUser(email, password, displayName);
      } else {
        await login(email, password);
      }
    } catch (err) {
      setError(err.message.replace('Firebase: ', ''));
    }
    setLoading(false);
  }

  return (
    <div style={{
      minHeight: '100vh', background: COLORS.bg, display: 'flex', alignItems: 'center',
      justifyContent: 'center', fontFamily: "'SF Mono', 'Fira Code', 'JetBrains Mono', ui-monospace, monospace",
    }}>
      <div style={{
        background: COLORS.bgCard, border: `1px solid ${COLORS.border}`, borderRadius: 20,
        padding: '48px 40px', width: '100%', maxWidth: 400, textAlign: 'center',
        boxShadow: `0 0 60px ${COLORS.greenGlow}`,
      }}>
        <div style={{
          width: 72, height: 72, borderRadius: 18, background: COLORS.greenGlow,
          border: `2px solid ${COLORS.greenDim}`, display: 'flex', alignItems: 'center',
          justifyContent: 'center', margin: '0 auto 20px',
          boxShadow: `0 0 30px ${COLORS.greenGlow}`,
        }}>
          <svg width="44" height="44" viewBox="0 0 100 100" fill="none">
            <line x1="38" y1="90" x2="47" y2="50" stroke={COLORS.green} strokeWidth="3"/>
            <line x1="62" y1="90" x2="53" y2="50" stroke={COLORS.green} strokeWidth="3"/>
            <line x1="41" y1="72" x2="59" y2="72" stroke={COLORS.green} strokeWidth="2"/>
            <line x1="50" y1="50" x2="50" y2="18" stroke={COLORS.green} strokeWidth="3.5"/>
            <circle cx="50" cy="18" r="6" fill={COLORS.green} opacity="0.8"/>
            <circle cx="50" cy="18" r="3" fill={COLORS.green}/>
            <circle cx="50" cy="18" r="14" stroke={COLORS.green} strokeWidth="2" fill="none" opacity="0.5"/>
            <circle cx="50" cy="18" r="24" stroke={COLORS.green} strokeWidth="1.5" fill="none" opacity="0.25"/>
            <rect x="32" y="88" width="36" height="5" rx="2.5" fill={COLORS.green}/>
          </svg>
        </div>
        <div style={{ fontSize: 24, fontWeight: 700, color: COLORS.green, letterSpacing: '0.12em', marginBottom: 2 }}>
          GROUND CONTROL
        </div>
        <div style={{ fontSize: 12, color: COLORS.textMuted, letterSpacing: '0.2em', marginBottom: 36 }}>
          SATELLITE GARDENS
        </div>

        {error && (
          <div style={{
            padding: '10px 14px', background: `${COLORS.danger}18`, border: `1px solid ${COLORS.danger}30`,
            borderRadius: 8, marginBottom: 16, fontSize: 13, color: COLORS.danger, textAlign: 'left',
          }}>{error}</div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, textAlign: 'left' }}>
          {isCreating && (
            <Input label="Display Name" value={displayName} onChange={setDisplayName} placeholder="Dylan" />
          )}
          <Input label="Email" value={email} onChange={setEmail} placeholder="dylan@satellitegardens.com" />
          <Input label="Password" value={password} onChange={setPassword} type="password" placeholder="••••••••" />
        </div>

        <button onClick={handleSubmit} disabled={loading}
          style={{
            width: '100%', padding: '14px', background: loading ? COLORS.greenDark : COLORS.green,
            color: COLORS.bg, border: 'none', borderRadius: 10, fontSize: 16, fontWeight: 700,
            cursor: loading ? 'wait' : 'pointer', marginTop: 24, fontFamily: 'inherit',
            letterSpacing: '0.05em', transition: 'all 0.15s ease',
          }}>
          {loading ? 'CONNECTING...' : isCreating ? 'CREATE ACCOUNT ▸' : 'ESTABLISH LINK ▸'}
        </button>

        <button onClick={() => setIsCreating(!isCreating)}
          style={{
            background: 'none', border: 'none', color: COLORS.textDim, cursor: 'pointer',
            fontSize: 13, fontFamily: 'inherit', marginTop: 16,
          }}>
          {isCreating ? '← Back to login' : 'First time? Create account'}
        </button>
      </div>
    </div>
  );
}

// ============================================================
// DASHBOARD PAGE
// ============================================================
function DashboardPage({ jobs, receipts, timeEntries, invoices, setPage, setSelectedJob, displayName }) {
  const activeJobs = jobs.filter(j => j.status === 'active');
  const recentReceipts = [...receipts].filter(r => r.status !== 'deleted' && r.status !== 'split').sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || '')).slice(0, 5);

  const paidTotal = invoices.filter(i => i.status === 'paid').reduce((s, i) => s + (i.grandTotal || 0), 0);
  const unbilledTotal = activeJobs.reduce((s, j) => {
    const t = computeJobTotals(j, receipts, timeEntries, invoices);
    return s + t.unbilledMaterials + t.unbilledLabor;
  }, 0);
  const thisWeekHours = timeEntries.filter(t => t.date >= weekStartISO()).reduce((s, t) => s + (t.hours || 0), 0);

  return (
    <div>
      <PageHeader title="Mission Control" subtitle="Satellite Gardens Command Center" currentUser={displayName}
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <Button icon="upload" onClick={() => setPage('upload')}>Upload Receipt</Button>
            <Button icon="clock" variant="secondary" onClick={() => setPage('time')}>Log Time</Button>
          </div>
        } />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 28 }}>
        {[
          { label: 'Active Jobs', value: activeJobs.length, icon: 'briefcase' },
          { label: 'This Week', value: `${thisWeekHours}h`, icon: 'clock', sub: 'hours logged' },
          { label: 'Unbilled', value: fmt(unbilledTotal), icon: 'dollarSign', sub: 'pending' },
          { label: 'Revenue (Paid)', value: fmt(paidTotal), icon: 'trendingUp' },
        ].map((s, i) => <GlowCard key={i}><Stat {...s} /></GlowCard>)}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: COLORS.text }}>Active Jobs</h3>
            <button onClick={() => setPage('jobs')} style={{ background: 'none', border: 'none', color: COLORS.green, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>View all →</button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {activeJobs.map(job => {
              const t = computeJobTotals(job, receipts, timeEntries, invoices);
              return (
                <GlowCard key={job.id} onClick={() => { setSelectedJob(job); setPage('jobDetail'); }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 600, color: COLORS.text }}>{job.clientName}</div>
                      <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>{job.notes}</div>
                    </div>
                    <Icon name="chevronRight" size={16} color={COLORS.textMuted} />
                  </div>
                  <div style={{ display: 'flex', gap: 20, marginTop: 12 }}>
                    <div>
                      <div style={{ fontSize: 11, color: COLORS.textMuted }}>Billed</div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.green }}>{fmt(t.billedTotal)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: COLORS.textMuted }}>Unbilled</div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.gold }}>{fmt(t.unbilledMaterials + t.unbilledLabor)}</div>
                    </div>
                  </div>
                </GlowCard>
              );
            })}
            {activeJobs.length === 0 && (
              <div style={{ textAlign: 'center', padding: 32, color: COLORS.textMuted }}>
                <p>No active jobs yet.</p>
                <Button icon="plus" onClick={() => setPage('jobs')} style={{ marginTop: 12 }}>Create First Job</Button>
              </div>
            )}
          </div>
        </div>

        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: COLORS.text }}>Recent Activity</h3>
            <button onClick={() => setPage('receipts')} style={{ background: 'none', border: 'none', color: COLORS.green, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>View all →</button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {recentReceipts.map(r => {
              const job = jobs.find(j => j.id === r.jobId);
              return (
                <GlowCard key={r.id}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{
                        width: 36, height: 36, borderRadius: 8, background: r.isOverhead ? `${COLORS.gold}18` : COLORS.greenGlow,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${COLORS.border}`,
                      }}>
                        <Icon name={r.isOverhead ? 'rocket' : 'leaf'} size={16} color={r.isOverhead ? COLORS.gold : COLORS.green} />
                      </div>
                      <div>
                        <div style={{ fontSize: 14, color: COLORS.text }}>{r.vendor}</div>
                        <div style={{ fontSize: 12, color: COLORS.textMuted }}>
                          {r.description} • {job ? job.clientName : 'Overhead'} • {r.createdBy}
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text }}>{fmt(r.amount)}</div>
                      <div style={{ fontSize: 11, color: COLORS.textMuted }}>{fmtShort(r.date)}</div>
                    </div>
                  </div>
                </GlowCard>
              );
            })}
            {recentReceipts.length === 0 && (
              <div style={{ textAlign: 'center', padding: 32, color: COLORS.textMuted }}>No receipts yet</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// JOBS PAGE — fire-and-forget save
// ============================================================
function JobsPage({ jobs, receipts, timeEntries, invoices, setPage, setSelectedJob, displayName, userId }) {
  const [filter, setFilter] = useState('active');
  const [showNewJob, setShowNewJob] = useState(false);
  const [newJob, setNewJob] = useState({ clientName: '', address: '', phone: '', email: '', hourlyRate: '', notes: '' });

  const filtered = jobs.filter(j => filter === 'all' ? true : j.status === filter);

  function handleCreate() {
    if (!newJob.clientName) return;
    // Fire and forget — close modal immediately
    createJob(newJob, userId);
    setNewJob({ clientName: '', address: '', phone: '', email: '', hourlyRate: '', notes: '' });
    setShowNewJob(false);
  }

  return (
    <div>
      <PageHeader title="Jobs" subtitle={`${jobs.filter(j=>j.status==='active').length} active`} currentUser={displayName}
        actions={<Button icon="plus" onClick={() => setShowNewJob(true)}>New Job</Button>} />

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {['active', 'completed', 'archived', 'all'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            style={{
              padding: '6px 16px', borderRadius: 20, border: `1px solid ${filter === f ? COLORS.green : COLORS.border}`,
              background: filter === f ? COLORS.greenGlow : 'transparent', color: filter === f ? COLORS.green : COLORS.textDim,
              cursor: 'pointer', fontSize: 13, fontWeight: 500, fontFamily: 'inherit', textTransform: 'capitalize',
            }}>{f}</button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14 }}>
        {filtered.map(job => {
          const t = computeJobTotals(job, receipts, timeEntries, invoices);
          return (
            <GlowCard key={job.id} onClick={() => { setSelectedJob(job); setPage('jobDetail'); }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: COLORS.text }}>{job.clientName}</div>
                  <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 3 }}>{job.address}</div>
                </div>
                <StatusBadge status={job.status} />
              </div>
              <div style={{ fontSize: 13, color: COLORS.textDim, marginBottom: 12 }}>{job.notes}</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 10, borderTop: `1px solid ${COLORS.border}` }}>
                <div>
                  <div style={{ fontSize: 11, color: COLORS.textMuted }}>Rate</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text }}>{fmt(job.hourlyRate)}/hr</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: COLORS.textMuted }}>Billed</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.green }}>{fmt(t.billedTotal)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: COLORS.textMuted }}>Unbilled</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.gold }}>{fmt(t.unbilledMaterials + t.unbilledLabor)}</div>
                </div>
              </div>
            </GlowCard>
          );
        })}
      </div>

      {showNewJob && (
        <Modal title="Create New Job" onClose={() => setShowNewJob(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Input label="Client Name *" value={newJob.clientName} onChange={v => setNewJob({...newJob, clientName: v})} placeholder="e.g. Adam Newport-Berra" />
            <Input label="Address" value={newJob.address} onChange={v => setNewJob({...newJob, address: v})} placeholder="Job site address" />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Input label="Phone" value={newJob.phone} onChange={v => setNewJob({...newJob, phone: v})} placeholder="503-555-0000" />
              <Input label="Email" value={newJob.email} onChange={v => setNewJob({...newJob, email: v})} placeholder="client@email.com" />
            </div>
            <Input label="Hourly Rate" value={newJob.hourlyRate} onChange={v => setNewJob({...newJob, hourlyRate: v})} prefix="$" type="number" placeholder="65" />
            <Input label="Notes" value={newJob.notes} onChange={v => setNewJob({...newJob, notes: v})} textarea placeholder="Brief job description..." />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
              <Button variant="ghost" onClick={() => setShowNewJob(false)}>Cancel</Button>
              <Button icon="check" onClick={handleCreate} disabled={!newJob.clientName}>Create Job</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ============================================================
// RECEIPT UPLOAD PAGE — multi-file queue + AI feedback
// ============================================================
function ReceiptUploadPage({ jobs, setPage, displayName, userId, showToast }) {
  const [step, setStep] = useState('upload');
  const [dragOver, setDragOver] = useState(false);
  const [file, setFile] = useState(null);
  const [aiError, setAiError] = useState('');
  const [review, setReview] = useState({
    vendor: '', date: todayISO(), amount: '', items: [],
    suggestedDescription: '', description: '', jobId: '', taxCategory: 'Construction Materials',
    confidence: '',
  });
  const [splitMode, setSplitMode] = useState(false);

  // Multi-file queue
  const [fileQueue, setFileQueue] = useState([]);
  const [queueIndex, setQueueIndex] = useState(0);

  const activeJobs = jobs.filter(j => j.status === 'active');
  const fileInputRef = React.useRef(null);

  async function processFile(f) {
    setFile(f);
    setStep('processing');
    setAiError('');

    let aiData = null;
    if (isAIConfigured()) {
      try {
        const { base64, mediaType } = await compressForAI(f);
        aiData = await extractReceiptData(base64, mediaType);
      } catch (aiErr) {
        console.warn('AI extraction failed:', aiErr.message);
        setAiError(aiErr.message);
      }
    }

    setReview({
      vendor: aiData?.vendor || '',
      date: aiData?.date || todayISO(),
      amount: aiData?.total ? String(aiData.total) : '',
      items: (aiData?.items || []).map(item => typeof item === 'string' ? { name: item, amount: 0, qty: 1, jobId: '' } : { ...item, jobId: '' }),
      suggestedDescription: aiData?.suggestedDescription || '',
      description: aiData?.suggestedDescription || '',
      jobId: '',
      taxCategory: aiData?.suggestedCategory || 'Construction Materials',
      confidence: aiData?.confidence || 'manual',
    });
    setSplitMode(false);
    setStep('review');
  }

  function handleFileSelect(e) {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (files.length === 1) {
      processFile(files[0]);
    } else {
      // Multi-file: queue them up
      setFileQueue(files);
      setQueueIndex(0);
      processFile(files[0]);
    }
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length === 0) return;
    if (files.length === 1) {
      processFile(files[0]);
    } else {
      setFileQueue(files);
      setQueueIndex(0);
      processFile(files[0]);
    }
  }

  function updateItemJob(index, jobId) {
    setReview(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? { ...item, jobId } : item),
    }));
  }

  function tagAllItemsUpload(jobId) {
    setReview(prev => ({
      ...prev,
      jobId,
      items: prev.items.map(item => ({ ...item, jobId })),
    }));
  }

  function handleSave() {
    if (!review.vendor || !review.amount) return;

    // Check if we're in split mode with items tagged to different jobs
    if (splitMode && review.items.length > 0) {
      const hasItemPrices = review.items.some(it => typeof it === 'object' && it.amount > 0);

      // Group items by jobId
      const groups = {};
      for (const item of review.items) {
        const jobId = (typeof item === 'object' ? item.jobId : review.jobId) || '';
        const key = jobId || '__overhead__';
        if (!groups[key]) groups[key] = { jobId: jobId || null, items: [], total: 0 };
        groups[key].items.push(item);
        if (hasItemPrices && typeof item === 'object') groups[key].total += (item.amount || 0);
      }

      const groupKeys = Object.keys(groups);

      // If all items on same job, just save as one receipt normally
      if (groupKeys.length <= 1) {
        const g = groups[groupKeys[0]];
        createReceipt({
          vendor: review.vendor, date: review.date,
          amount: parseFloat(review.amount),
          description: review.description,
          jobId: g.jobId, taxCategory: review.taxCategory,
          driveFileId: null, driveFileUrl: null,
          aiExtractedItems: review.items, aiConfidence: review.confidence,
        }, displayName);
      } else {
        // Calculate tax/remainder
        const itemsTotal = hasItemPrices ? review.items.reduce((s, it) => s + ((typeof it === 'object' ? it.amount : 0) || 0), 0) : 0;
        const receiptTotal = parseFloat(review.amount);
        const tax = hasItemPrices ? receiptTotal - itemsTotal : 0;

        // Create a receipt per group
        let remaining = receiptTotal;
        groupKeys.forEach((key, i) => {
          const g = groups[key];
          let amount;
          if (hasItemPrices) {
            amount = g.total;
            // Add proportional share of tax
            if (tax > 0 && itemsTotal > 0) {
              amount += tax * (g.total / itemsTotal);
            }
          } else {
            // Proportional by item count
            const proportion = g.items.length / review.items.length;
            amount = i === groupKeys.length - 1 ? remaining : receiptTotal * proportion;
          }
          amount = parseFloat(amount.toFixed(2));
          remaining -= amount;

          const desc = g.items.map(it => typeof it === 'object' ? it.name : it).join(', ');
          createReceipt({
            vendor: review.vendor, date: review.date, amount,
            description: desc.length > 80 ? desc.slice(0, 77) + '...' : desc,
            jobId: g.jobId, taxCategory: review.taxCategory,
            driveFileId: null, driveFileUrl: null,
            aiExtractedItems: g.items, aiConfidence: review.confidence,
          }, displayName);
        });

        showToast(`Receipt split into ${groupKeys.length} entries!`);
        // Drive upload in background
        if (file && hasDriveAccess()) {
          const jobName = activeJobs.find(j => j.id === review.jobId)?.clientName;
          uploadReceiptFile(file, {
            date: review.date, vendor: review.vendor,
            description: review.description, jobName: jobName || null,
          }).catch(err => console.warn('Drive upload failed:', err.message));
        }
        // Advance queue or finish
        if (fileQueue.length > 0 && queueIndex < fileQueue.length - 1) {
          const nextIndex = queueIndex + 1;
          setQueueIndex(nextIndex);
          setSplitMode(false);
          processFile(fileQueue[nextIndex]);
        } else {
          setStep('success');
          setFileQueue([]);
          setQueueIndex(0);
          setSplitMode(false);
        }
        return;
      }
    } else {
      // Normal single-receipt save
      createReceipt({
        vendor: review.vendor, date: review.date,
        amount: parseFloat(review.amount),
        description: review.description,
        jobId: review.jobId || null, taxCategory: review.taxCategory,
        driveFileId: null, driveFileUrl: null,
        aiExtractedItems: review.items, aiConfidence: review.confidence,
      }, displayName);
    }

    // Drive upload in background
    if (file && hasDriveAccess()) {
      const jobName = activeJobs.find(j => j.id === review.jobId)?.clientName;
      uploadReceiptFile(file, {
        date: review.date, vendor: review.vendor,
        description: review.description, jobName: jobName || null,
      }).catch(err => console.warn('Drive upload failed:', err.message));
    }

    showToast('Receipt saved!');

    // If there are more files in the queue, process next
    if (fileQueue.length > 0 && queueIndex < fileQueue.length - 1) {
      const nextIndex = queueIndex + 1;
      setQueueIndex(nextIndex);
      setSplitMode(false);
      processFile(fileQueue[nextIndex]);
    } else {
      setStep('success');
      setFileQueue([]);
      setQueueIndex(0);
      setSplitMode(false);
    }
  }

  function resetForAnother() {
    setFile(null);
    setStep('upload');
    setAiError('');
    setFileQueue([]);
    setQueueIndex(0);
    setSplitMode(false);
    setReview({
      vendor: '', date: todayISO(), amount: '', items: [],
      suggestedDescription: '', description: '', jobId: '', taxCategory: 'Construction Materials',
      confidence: '',
    });
  }

  const aiStatus = getAIStatus();

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <button onClick={() => setPage('dashboard')} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, color: COLORS.textDim, fontFamily: 'inherit', fontSize: 14 }}>
          <Icon name="chevronLeft" size={16} color={COLORS.textDim} /> Dashboard
        </button>
      </div>
      <PageHeader title="Upload Receipts" subtitle="Capture, extract, and categorize" currentUser={displayName} />

      <input ref={fileInputRef} type="file" accept="image/*,.pdf" capture="environment" multiple
        style={{ display: 'none' }} onChange={handleFileSelect} />

      {step === 'upload' && (
        <GlowCard glow>
          <div onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={handleDrop}
            style={{
              border: `2px dashed ${dragOver ? COLORS.green : COLORS.border}`, borderRadius: 12,
              padding: '60px 40px', textAlign: 'center', background: dragOver ? COLORS.greenGlow : 'transparent',
              transition: 'all 0.2s ease', cursor: 'pointer',
            }}
            onClick={() => fileInputRef.current?.click()}>
            <div style={{
              width: 72, height: 72, borderRadius: 20, background: COLORS.greenGlow,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 20px', border: `1px solid ${COLORS.border}`,
            }}>
              <Icon name="upload" size={32} color={COLORS.green} />
            </div>
            <div style={{ fontSize: 18, fontWeight: 600, color: COLORS.text, marginBottom: 8 }}>
              Drop receipts here or tap to upload
            </div>
            <div style={{ fontSize: 14, color: COLORS.textMuted, marginBottom: 8 }}>
              Select multiple files to process them in a batch
            </div>
            <div style={{ fontSize: 13, color: COLORS.textMuted, marginBottom: 20 }}>
              Supports JPG, PNG, HEIC, PDF — up to 10MB each
            </div>
            {aiStatus !== 'ready' && (
              <div style={{ padding: '8px 14px', background: `${COLORS.warning}18`, borderRadius: 8, border: `1px solid ${COLORS.warning}30`, fontSize: 13, color: COLORS.warning, marginBottom: 12 }}>
                AI status: {aiStatus}
              </div>
            )}
            {aiStatus === 'ready' && (
              <div style={{ padding: '8px 14px', background: COLORS.greenGlow, borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 13, color: COLORS.green, marginBottom: 12 }}>
                AI extraction ready
              </div>
            )}
          </div>
        </GlowCard>
      )}

      {step === 'processing' && (
        <GlowCard glow>
          <Spinner size={72} label={isAIConfigured() ? 'AI is reading the receipt...' : 'Processing...'} />
          {fileQueue.length > 1 && (
            <div style={{ textAlign: 'center', marginTop: 12, fontSize: 14, color: COLORS.textDim }}>
              Receipt {queueIndex + 1} of {fileQueue.length}
            </div>
          )}
        </GlowCard>
      )}

      {step === 'review' && (
        <GlowCard>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <Icon name={review.confidence === 'manual' ? 'edit' : 'checkCircle'} size={20} color={COLORS.green} />
            <span style={{ fontSize: 14, fontWeight: 600, color: COLORS.green }}>
              {review.confidence === 'manual' ? 'Manual entry' : 'Receipt extracted'}
            </span>
            {review.confidence && review.confidence !== 'manual' && <Badge color={COLORS.green}>{review.confidence} confidence</Badge>}
            {fileQueue.length > 1 && (
              <Badge color={COLORS.gold}>{queueIndex + 1} of {fileQueue.length}</Badge>
            )}
          </div>

          {aiError && (
            <div style={{ padding: '8px 14px', background: `${COLORS.danger}18`, borderRadius: 8, border: `1px solid ${COLORS.danger}30`, fontSize: 13, color: COLORS.danger, marginBottom: 14 }}>
              AI error: {aiError}
            </div>
          )}

          {file && (
            <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 14 }}>
              File: {file.name} ({Math.round(file.size / 1024)}KB)
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Input label="Vendor" value={review.vendor} onChange={v => setReview({...review, vendor: v})} placeholder="Store name" />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Input label="Date" value={review.date} onChange={v => setReview({...review, date: v})} type="date" />
              <Input label="Amount" value={review.amount} onChange={v => setReview({...review, amount: v})} prefix="$" type="number" placeholder="0.00" />
            </div>

            {review.items.length > 0 && !splitMode && (
              <div>
                <label style={{ fontSize: 13, color: COLORS.textDim, fontWeight: 500 }}>Items Found</label>
                <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {review.items.map((item, i) => {
                    const name = typeof item === 'string' ? item : item.name;
                    const amount = typeof item === 'object' && item.amount ? item.amount : null;
                    const qty = typeof item === 'object' && item.qty > 1 ? item.qty : null;
                    return (
                      <div key={i} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '5px 10px', borderRadius: 6, background: COLORS.greenGlow,
                        border: `1px solid ${COLORS.border}`, fontSize: 12, color: COLORS.text,
                      }}>
                        <span>{qty ? `${qty}× ` : ''}{name}</span>
                        {amount > 0 && <span style={{ fontWeight: 600, color: COLORS.green }}>{fmt(amount)}</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* SPLIT MODE — tag each item to a job */}
            {splitMode && review.items.length > 0 && (
              <div>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10,
                  padding: '8px 12px', background: COLORS.bgCard, borderRadius: 8,
                  border: `1px solid ${COLORS.border}`,
                }}>
                  <span style={{ fontSize: 13, color: COLORS.textDim, whiteSpace: 'nowrap' }}>Tag all to:</span>
                  <select onChange={e => tagAllItemsUpload(e.target.value)}
                    style={{
                      flex: 1, background: COLORS.bgInput, border: `1px solid ${COLORS.border}`,
                      borderRadius: 6, padding: '6px 10px', color: COLORS.text, fontSize: 13,
                      fontFamily: 'inherit', outline: 'none', cursor: 'pointer',
                    }}>
                    <option value="">— pick to tag all —</option>
                    <option value="">Overhead (no job)</option>
                    {activeJobs.map(j => <option key={j.id} value={j.id}>{j.clientName}</option>)}
                  </select>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {review.items.map((item, i) => {
                    const name = typeof item === 'string' ? item : item.name;
                    const amount = typeof item === 'object' && item.amount ? item.amount : null;
                    const qty = typeof item === 'object' && item.qty > 1 ? item.qty : null;
                    const itemJobId = typeof item === 'object' ? (item.jobId || '') : '';
                    return (
                      <div key={i} style={{
                        display: 'grid', gridTemplateColumns: amount > 0 ? '1fr 70px 1fr' : '1fr 1fr',
                        gap: 8, alignItems: 'center', padding: '6px 10px', borderRadius: 6,
                        background: COLORS.bgCard, border: `1px solid ${COLORS.border}`,
                        borderLeft: `3px solid ${itemJobId ? COLORS.green : COLORS.gold}`,
                      }}>
                        <div style={{ fontSize: 12, color: COLORS.text }}>{qty ? `${qty}× ` : ''}{name}</div>
                        {amount > 0 && <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.green, textAlign: 'right' }}>{fmt(amount)}</div>}
                        <select value={itemJobId} onChange={e => updateItemJob(i, e.target.value)}
                          style={{
                            background: COLORS.bgInput, border: `1px solid ${COLORS.border}`,
                            borderRadius: 6, padding: '5px 8px', color: itemJobId ? COLORS.text : COLORS.textMuted,
                            fontSize: 12, fontFamily: 'inherit', outline: 'none', cursor: 'pointer', width: '100%',
                          }}>
                          <option value="">Overhead</option>
                          {activeJobs.map(j => <option key={j.id} value={j.id}>{j.clientName}</option>)}
                        </select>
                      </div>
                    );
                  })}
                </div>

                {/* Split summary */}
                {(() => {
                  const hasItemPrices = review.items.some(it => typeof it === 'object' && it.amount > 0);
                  const groups = {};
                  for (const item of review.items) {
                    const jobId = (typeof item === 'object' ? item.jobId : '') || '';
                    const key = jobId || '__overhead__';
                    const label = jobId ? (activeJobs.find(j => j.id === jobId)?.clientName || 'Unknown') : 'Overhead';
                    if (!groups[key]) groups[key] = { label, total: 0, count: 0 };
                    if (hasItemPrices && typeof item === 'object') groups[key].total += (item.amount || 0);
                    groups[key].count++;
                  }
                  const groupEntries = Object.entries(groups);
                  if (groupEntries.length <= 1) return null;
                  return (
                    <div style={{
                      marginTop: 10, padding: '10px 14px', borderRadius: 8,
                      background: COLORS.greenGlow, border: `1px solid ${COLORS.border}`,
                    }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, letterSpacing: '0.05em', marginBottom: 6 }}>
                        WILL CREATE {groupEntries.length} RECEIPTS
                      </div>
                      {groupEntries.map(([key, g]) => (
                        <div key={key} style={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Icon name={key === '__overhead__' ? 'rocket' : 'leaf'} size={12}
                              color={key === '__overhead__' ? COLORS.gold : COLORS.green} />
                            <span style={{ fontSize: 12, color: COLORS.text }}>{g.label}</span>
                            <span style={{ fontSize: 11, color: COLORS.textMuted }}>({g.count} items)</span>
                          </div>
                          {hasItemPrices && <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.green }}>{fmt(g.total)}</span>}
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Split toggle button — only show when items exist */}
            {review.items.length > 1 && (
              <button onClick={() => setSplitMode(!splitMode)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  width: '100%', padding: '8px', background: splitMode ? `${COLORS.gold}18` : 'transparent',
                  border: `1px dashed ${splitMode ? COLORS.gold : COLORS.border}`, borderRadius: 8,
                  color: splitMode ? COLORS.gold : COLORS.textDim, cursor: 'pointer',
                  fontSize: 13, fontFamily: 'inherit',
                }}>
                <Icon name="plus" size={14} color={splitMode ? COLORS.gold : COLORS.textDim} />
                {splitMode ? 'Exit split mode' : 'Split items to different jobs'}
              </button>
            )}

            {review.suggestedDescription && (
              <div style={{
                padding: '10px 14px', background: `${COLORS.gold}10`, borderRadius: 8,
                border: `1px solid ${COLORS.gold}30`, fontSize: 13, color: COLORS.gold,
              }}>
                AI suggests: "{review.suggestedDescription}"
              </div>
            )}

            <Input label="Your Description" value={review.description} onChange={v => setReview({...review, description: v})} placeholder="Your shorthand description" />

            {!splitMode && (
              <Select label="Assign to Job" value={review.jobId} onChange={v => setReview({...review, jobId: v})}
                options={[{ value: '', label: 'Overhead (no job)' }, ...activeJobs.map(j => ({ value: j.id, label: j.clientName }))]} />
            )}

            <Select label="Tax Category" value={review.taxCategory} onChange={v => setReview({...review, taxCategory: v})}
              options={TAX_CATEGORIES} />

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
              <Button variant="ghost" onClick={resetForAnother}>Cancel</Button>
              <Button icon="check" onClick={handleSave} disabled={!review.vendor || !review.amount}>
                {splitMode
                  ? (() => {
                      const jobIds = new Set(review.items.map(it => (typeof it === 'object' ? it.jobId : '') || '__oh__'));
                      return jobIds.size > 1 ? `Save ${jobIds.size} Split Receipts` : 'Save Receipt';
                    })()
                  : fileQueue.length > 1 && queueIndex < fileQueue.length - 1
                    ? `Save & Next (${queueIndex + 1}/${fileQueue.length})`
                    : 'Save Receipt'}
              </Button>
            </div>
          </div>
        </GlowCard>
      )}

      {step === 'success' && (
        <GlowCard glow>
          <div style={{ textAlign: 'center', padding: '48px 40px' }}>
            <div style={{
              width: 72, height: 72, borderRadius: '50%', background: COLORS.greenGlow,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 20px', border: `2px solid ${COLORS.green}`,
            }}>
              <Icon name="check" size={36} color={COLORS.green} />
            </div>
            <div style={{ fontSize: 20, fontWeight: 700, color: COLORS.text, marginBottom: 8 }}>Receipt saved!</div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 28 }}>
              <Button icon="upload" onClick={resetForAnother}>Upload More</Button>
              <Button variant="secondary" onClick={() => setPage('dashboard')}>Back to Dashboard</Button>
            </div>
          </div>
        </GlowCard>
      )}
    </div>
  );
}

// ============================================================
// RECEIPTS LIST PAGE
// ============================================================
function ReceiptsPage({ receipts, jobs, setPage, displayName, userId, showToast }) {
  const [jobFilter, setJobFilter] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [editReceipt, setEditReceipt] = useState(null);
  const [splitReceiptData, setSplitReceiptData] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const activeReceipts = receipts.filter(r => r.status !== 'deleted' && r.status !== 'split');
  const filtered = activeReceipts.filter(r => {
    if (jobFilter === 'overhead' && !r.isOverhead) return false;
    if (jobFilter && jobFilter !== 'overhead' && r.jobId !== jobFilter) return false;
    if (catFilter && r.taxCategory !== catFilter) return false;
    return true;
  });
  const total = filtered.reduce((s, r) => s + (r.amount || 0), 0);
  const activeJobs = jobs.filter(j => j.status === 'active');

  function handleEditSave() {
    if (!editReceipt) return;
    const updates = {
      vendor: editReceipt.vendor,
      date: editReceipt.date,
      amount: parseFloat(editReceipt.amount) || 0,
      description: editReceipt.description,
      jobId: editReceipt.jobId || null,
      isOverhead: !editReceipt.jobId,
      taxCategory: editReceipt.taxCategory,
      isSubcontractor: editReceipt.isSubcontractor || false,
    };
    updateReceipt(editReceipt.id, updates, userId);
    showToast('Receipt updated!');
    setEditReceipt(null);
  }

  function handleDelete() {
    if (!confirmDelete) return;
    deleteReceipt(confirmDelete.id);
    showToast('Receipt deleted');
    setConfirmDelete(null);
  }

  function openSplit(receipt) {
    // Build taggable line items from AI-extracted items
    const items = (receipt.aiExtractedItems || []).map((item, i) => {
      const isObj = typeof item === 'object' && item !== null;
      return {
        id: String(i),
        name: isObj ? (item.name || '') : String(item),
        amount: isObj ? (parseFloat(item.amount) || 0) : 0,
        qty: isObj ? (parseInt(item.qty) || 1) : 1,
        jobId: receipt.jobId || '',  // default to current assignment
        taxCategory: receipt.taxCategory || 'Construction Materials',
      };
    });

    // Calculate how much of the total the items account for
    const itemsTotal = items.reduce((s, it) => s + it.amount, 0);
    const tax = receipt.amount - itemsTotal;

    setSplitReceiptData({
      original: receipt,
      items,
      hasItemPrices: items.some(it => it.amount > 0),
      tax: tax > 0.005 ? parseFloat(tax.toFixed(2)) : 0,
      taxJobId: receipt.jobId || '',  // where to assign tax
    });
  }

  function updateSplitItem(id, field, value) {
    setSplitReceiptData(prev => ({
      ...prev,
      items: prev.items.map(it => it.id === id ? { ...it, [field]: value } : it),
    }));
  }

  function tagAllItems(jobId) {
    setSplitReceiptData(prev => ({
      ...prev,
      items: prev.items.map(it => ({ ...it, jobId })),
      taxJobId: jobId,
    }));
  }

  function handleSplitSave() {
    if (!splitReceiptData) return;
    const { original, items, tax, taxJobId, hasItemPrices } = splitReceiptData;

    if (!hasItemPrices) {
      // Proportional split: divide receipt total by item count per job group
      const totalItems = items.length;
      const groups = {};
      for (const item of items) {
        const key = item.jobId || '__overhead__';
        if (!groups[key]) groups[key] = { jobId: item.jobId || null, items: [], taxCategory: item.taxCategory };
        groups[key].items.push(item);
      }

      const groupKeys = Object.keys(groups);
      if (groupKeys.length <= 1) {
        const g = groups[groupKeys[0]];
        updateReceipt(original.id, { jobId: g.jobId, isOverhead: !g.jobId, taxCategory: g.taxCategory }, userId);
        showToast('Receipt re-assigned!');
        setSplitReceiptData(null);
        return;
      }

      // Distribute amount proportionally by item count
      let remaining = original.amount;
      const splitItems = groupKeys.map((key, i) => {
        const g = groups[key];
        const proportion = g.items.length / totalItems;
        const amount = i === groupKeys.length - 1
          ? parseFloat(remaining.toFixed(2))  // last group gets remainder to avoid rounding drift
          : parseFloat((original.amount * proportion).toFixed(2));
        remaining -= amount;
        const desc = g.items.map(it => it.name).join(', ');
        return {
          vendor: original.vendor,
          date: original.date,
          amount,
          description: desc.length > 80 ? desc.slice(0, 77) + '...' : desc,
          jobId: g.jobId,
          taxCategory: g.taxCategory,
          isSubcontractor: original.isSubcontractor || false,
        };
      });

      splitReceipt(original.id, splitItems, userId);
      showToast(`Receipt split proportionally into ${splitItems.length} items!`);
      setSplitReceiptData(null);
      return;
    }

    // Group items by jobId
    const groups = {};
    for (const item of items) {
      const key = item.jobId || '__overhead__';
      if (!groups[key]) groups[key] = { jobId: item.jobId || null, items: [], total: 0, taxCategory: item.taxCategory };
      groups[key].items.push(item);
      groups[key].total += item.amount;
    }

    // Add tax to the designated group
    if (tax > 0) {
      const taxKey = taxJobId || '__overhead__';
      if (!groups[taxKey]) groups[taxKey] = { jobId: taxJobId || null, items: [], total: 0, taxCategory: original.taxCategory };
      groups[taxKey].total += tax;
    }

    const groupKeys = Object.keys(groups);
    // If everything is tagged to the same job, just update the receipt instead of splitting
    if (groupKeys.length === 1) {
      const g = groups[groupKeys[0]];
      updateReceipt(original.id, {
        jobId: g.jobId,
        isOverhead: !g.jobId,
        taxCategory: g.taxCategory,
      }, userId);
      showToast('Receipt re-assigned!');
      setSplitReceiptData(null);
      return;
    }

    // Build split items for each group
    const splitItems = groupKeys.map(key => {
      const g = groups[key];
      const desc = g.items.map(it => it.name).join(', ');
      return {
        vendor: original.vendor,
        date: original.date,
        amount: parseFloat(g.total.toFixed(2)),
        description: desc.length > 80 ? desc.slice(0, 77) + '...' : desc,
        jobId: g.jobId,
        taxCategory: g.taxCategory,
        isSubcontractor: original.isSubcontractor || false,
      };
    });

    // Verify totals match
    const splitTotal = splitItems.reduce((s, it) => s + it.amount, 0);
    const diff = Math.abs(splitTotal - original.amount);
    if (diff > 0.02) {
      showToast(`Split total (${fmt(splitTotal)}) doesn't match receipt (${fmt(original.amount)}). Difference: ${fmt(diff)}`, 'warning');
    }

    splitReceipt(original.id, splitItems, userId);
    showToast(`Receipt split into ${splitItems.length} items!`);
    setSplitReceiptData(null);
  }

  return (
    <div>
      <PageHeader title="Receipts" subtitle={`${activeReceipts.length} total`} currentUser={displayName}
        actions={<Button icon="upload" onClick={() => setPage('upload')}>Upload Receipt</Button>} />

      <GlowCard style={{ marginBottom: 20 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 12, alignItems: 'end' }}>
          <Select label="Job" value={jobFilter} onChange={setJobFilter}
            options={[{ value: '', label: 'All Jobs' }, { value: 'overhead', label: 'Overhead' }, ...jobs.map(j => ({ value: j.id, label: j.clientName }))]} />
          <Select label="Category" value={catFilter} onChange={setCatFilter}
            options={[{ value: '', label: 'All Categories' }, ...TAX_CATEGORIES.map(c => ({ value: c, label: c }))]} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingBottom: 2 }}>
            <div style={{ fontSize: 14, color: COLORS.textDim }}>{filtered.length}</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.green }}>{fmt(total)}</div>
          </div>
        </div>
      </GlowCard>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {filtered.sort((a, b) => (b.date || '').localeCompare(a.date || '')).map(r => {
          const job = jobs.find(j => j.id === r.jobId);
          return (
            <GlowCard key={r.id}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 0 }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: 10, background: r.isOverhead ? `${COLORS.gold}18` : COLORS.greenGlow,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${COLORS.border}`, flexShrink: 0,
                  }}>
                    <Icon name={r.isOverhead ? 'rocket' : 'leaf'} size={18} color={r.isOverhead ? COLORS.gold : COLORS.green} />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: COLORS.text }}>{r.vendor}</div>
                    <div style={{ fontSize: 13, color: COLORS.textDim }}>
                      {r.description} • <span style={{ color: COLORS.textMuted }}>{job ? job.clientName : 'Overhead'}</span>
                    </div>
                    <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>
                      {fmtShort(r.date)} • {r.createdBy}
                      {r.splitFromId && <span style={{ color: COLORS.gold }}> • split</span>}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.text }}>{fmt(r.amount)}</div>
                    {!r.isOverhead && <div style={{ fontSize: 12, color: COLORS.textMuted }}>+10% = {fmt(r.markedUpAmount || r.amount * 1.1)}</div>}
                    <Badge color={r.billed ? COLORS.green : COLORS.gold}>{r.billed ? 'Billed' : 'Unbilled'}</Badge>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <button onClick={() => setEditReceipt({ ...r })}
                      title="Edit receipt"
                      style={{
                        background: COLORS.greenGlow, border: `1px solid ${COLORS.border}`, borderRadius: 6,
                        padding: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                      <Icon name="edit" size={14} color={COLORS.green} />
                    </button>
                    {!r.billed && (
                      <button onClick={() => openSplit(r)}
                        title="Split receipt"
                        style={{
                          background: `${COLORS.gold}18`, border: `1px solid ${COLORS.border}`, borderRadius: 6,
                          padding: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                        <Icon name="plus" size={14} color={COLORS.gold} />
                      </button>
                    )}
                    <button onClick={() => setConfirmDelete(r)}
                      title="Delete receipt"
                      style={{
                        background: `${COLORS.danger}18`, border: `1px solid ${COLORS.border}`, borderRadius: 6,
                        padding: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                      <Icon name="trash" size={14} color={COLORS.danger} />
                    </button>
                  </div>
                </div>
              </div>
            </GlowCard>
          );
        })}
        {filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: 40, color: COLORS.textMuted }}>No receipts match filters</div>
        )}
      </div>

      {/* EDIT RECEIPT MODAL */}
      {editReceipt && (
        <Modal title="Edit Receipt" onClose={() => setEditReceipt(null)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Input label="Vendor" value={editReceipt.vendor} onChange={v => setEditReceipt({...editReceipt, vendor: v})} placeholder="Store name" />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Input label="Date" value={editReceipt.date} onChange={v => setEditReceipt({...editReceipt, date: v})} type="date" />
              <Input label="Amount" value={String(editReceipt.amount)} onChange={v => setEditReceipt({...editReceipt, amount: v})} prefix="$" type="number" placeholder="0.00" />
            </div>
            <Input label="Description" value={editReceipt.description || ''} onChange={v => setEditReceipt({...editReceipt, description: v})} placeholder="Description" />
            <Select label="Assign to Job" value={editReceipt.jobId || ''} onChange={v => setEditReceipt({...editReceipt, jobId: v})}
              options={[{ value: '', label: 'Overhead (no job)' }, ...activeJobs.map(j => ({ value: j.id, label: j.clientName }))]} />
            <Select label="Tax Category" value={editReceipt.taxCategory || ''} onChange={v => setEditReceipt({...editReceipt, taxCategory: v})}
              options={TAX_CATEGORIES} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input type="checkbox" checked={editReceipt.isSubcontractor || false}
                onChange={e => setEditReceipt({...editReceipt, isSubcontractor: e.target.checked})}
                style={{ width: 18, height: 18, accentColor: COLORS.green }} />
              <label style={{ fontSize: 14, color: COLORS.textDim }}>Subcontractor expense</label>
            </div>
            {editReceipt.billed && (
              <div style={{
                padding: '10px 14px', background: `${COLORS.warning}18`, borderRadius: 8,
                border: `1px solid ${COLORS.warning}30`, fontSize: 13, color: COLORS.warning,
              }}>
                This receipt has already been billed. Editing won't change the invoice.
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
              <Button variant="ghost" onClick={() => setEditReceipt(null)}>Cancel</Button>
              <Button icon="check" onClick={handleEditSave}>Save Changes</Button>
            </div>
          </div>
        </Modal>
      )}

      {/* SPLIT RECEIPT MODAL */}
      {splitReceiptData && (
        <Modal title="Split Receipt by Line Items" onClose={() => setSplitReceiptData(null)} wide>
          {/* Header: original receipt info */}
          <div style={{
            padding: '12px 16px', background: COLORS.greenGlow, borderRadius: 8,
            border: `1px solid ${COLORS.border}`, marginBottom: 16,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text }}>{splitReceiptData.original.vendor}</div>
              <div style={{ fontSize: 12, color: COLORS.textMuted }}>
                {fmtShort(splitReceiptData.original.date)} • {splitReceiptData.original.description}
              </div>
            </div>
            <div style={{ fontSize: 20, fontWeight: 700, color: COLORS.green }}>
              {fmt(splitReceiptData.original.amount)}
            </div>
          </div>

          {/* No items warning */}
          {splitReceiptData.items.length === 0 && (
            <div style={{
              padding: '16px', background: `${COLORS.warning}18`, borderRadius: 8,
              border: `1px solid ${COLORS.warning}30`, fontSize: 13, color: COLORS.warning,
              textAlign: 'center', marginBottom: 16,
            }}>
              No line items were extracted from this receipt. Try re-uploading with a clearer photo, or use the Edit button to reassign the whole receipt.
            </div>
          )}

          {/* No prices warning */}
          {splitReceiptData.items.length > 0 && !splitReceiptData.hasItemPrices && (
            <div style={{
              padding: '16px', background: `${COLORS.warning}18`, borderRadius: 8,
              border: `1px solid ${COLORS.warning}30`, fontSize: 13, color: COLORS.warning,
              textAlign: 'center', marginBottom: 16,
            }}>
              AI couldn't read individual prices. Tag items to jobs and the total will be split proportionally by item count.
            </div>
          )}

          {splitReceiptData.items.length > 0 && (
            <>
              {/* Quick tag all */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14,
                padding: '8px 12px', background: COLORS.bgCard, borderRadius: 8,
                border: `1px solid ${COLORS.border}`,
              }}>
                <span style={{ fontSize: 13, color: COLORS.textDim, whiteSpace: 'nowrap' }}>Tag all items to:</span>
                <select
                  onChange={e => tagAllItems(e.target.value)}
                  style={{
                    flex: 1, background: COLORS.bgInput, border: `1px solid ${COLORS.border}`,
                    borderRadius: 6, padding: '6px 10px', color: COLORS.text, fontSize: 13,
                    fontFamily: 'inherit', outline: 'none', cursor: 'pointer',
                  }}>
                  <option value="">— pick to tag all —</option>
                  <option value="">Overhead (no job)</option>
                  {activeJobs.map(j => <option key={j.id} value={j.id}>{j.clientName}</option>)}
                </select>
              </div>

              {/* Line items table header */}
              <div style={{
                display: 'grid', gridTemplateColumns: splitReceiptData.hasItemPrices ? '1fr 80px 1fr' : '1fr 1fr',
                gap: 10, padding: '6px 12px', marginBottom: 4,
              }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, letterSpacing: '0.05em' }}>ITEM</div>
                {splitReceiptData.hasItemPrices && (
                  <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, letterSpacing: '0.05em', textAlign: 'right' }}>AMOUNT</div>
                )}
                <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, letterSpacing: '0.05em' }}>ASSIGN TO</div>
              </div>

              {/* Line items */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 350, overflowY: 'auto' }}>
                {splitReceiptData.items.map(item => {
                  const job = activeJobs.find(j => j.id === item.jobId);
                  return (
                    <div key={item.id} style={{
                      display: 'grid',
                      gridTemplateColumns: splitReceiptData.hasItemPrices ? '1fr 80px 1fr' : '1fr 1fr',
                      gap: 10, alignItems: 'center', padding: '8px 12px',
                      background: COLORS.bgCard, borderRadius: 8,
                      border: `1px solid ${COLORS.border}`,
                      borderLeft: `3px solid ${item.jobId ? COLORS.green : COLORS.gold}`,
                    }}>
                      <div>
                        <div style={{ fontSize: 13, color: COLORS.text }}>{item.qty > 1 ? `${item.qty}× ` : ''}{item.name}</div>
                      </div>
                      {splitReceiptData.hasItemPrices && (
                        <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text, textAlign: 'right' }}>
                          {fmt(item.amount)}
                        </div>
                      )}
                      <select value={item.jobId} onChange={e => updateSplitItem(item.id, 'jobId', e.target.value)}
                        style={{
                          background: COLORS.bgInput, border: `1px solid ${COLORS.border}`,
                          borderRadius: 6, padding: '6px 8px', color: item.jobId ? COLORS.text : COLORS.textMuted,
                          fontSize: 13, fontFamily: 'inherit', outline: 'none', cursor: 'pointer', width: '100%',
                        }}>
                        <option value="">Overhead</option>
                        {activeJobs.map(j => <option key={j.id} value={j.id}>{j.clientName}</option>)}
                      </select>
                    </div>
                  );
                })}

                {/* Tax row */}
                {splitReceiptData.tax > 0 && (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: splitReceiptData.hasItemPrices ? '1fr 80px 1fr' : '1fr 1fr',
                    gap: 10, alignItems: 'center', padding: '8px 12px',
                    background: COLORS.bgCard, borderRadius: 8,
                    border: `1px solid ${COLORS.border}`, borderLeft: `3px solid ${COLORS.textMuted}`,
                  }}>
                    <div style={{ fontSize: 13, color: COLORS.textMuted, fontStyle: 'italic' }}>Tax</div>
                    {splitReceiptData.hasItemPrices && (
                      <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.textMuted, textAlign: 'right' }}>
                        {fmt(splitReceiptData.tax)}
                      </div>
                    )}
                    <select value={splitReceiptData.taxJobId}
                      onChange={e => setSplitReceiptData(prev => ({ ...prev, taxJobId: e.target.value }))}
                      style={{
                        background: COLORS.bgInput, border: `1px solid ${COLORS.border}`,
                        borderRadius: 6, padding: '6px 8px', color: COLORS.textMuted,
                        fontSize: 13, fontFamily: 'inherit', outline: 'none', cursor: 'pointer', width: '100%',
                      }}>
                      <option value="">Overhead</option>
                      {activeJobs.map(j => <option key={j.id} value={j.id}>{j.clientName}</option>)}
                    </select>
                  </div>
                )}
              </div>

              {/* Grouped totals summary */}
              {splitReceiptData.hasItemPrices && (() => {
                const groups = {};
                for (const item of splitReceiptData.items) {
                  const key = item.jobId || '__overhead__';
                  const label = item.jobId ? (activeJobs.find(j => j.id === item.jobId)?.clientName || 'Unknown Job') : 'Overhead';
                  if (!groups[key]) groups[key] = { label, total: 0, count: 0 };
                  groups[key].total += item.amount;
                  groups[key].count++;
                }
                if (splitReceiptData.tax > 0) {
                  const taxKey = splitReceiptData.taxJobId || '__overhead__';
                  const taxLabel = splitReceiptData.taxJobId ? (activeJobs.find(j => j.id === splitReceiptData.taxJobId)?.clientName || 'Unknown Job') : 'Overhead';
                  if (!groups[taxKey]) groups[taxKey] = { label: taxLabel, total: 0, count: 0 };
                  groups[taxKey].total += splitReceiptData.tax;
                }
                const groupEntries = Object.entries(groups);
                const allSameJob = groupEntries.length <= 1;

                return (
                  <div style={{
                    marginTop: 14, padding: '12px 16px', borderRadius: 8,
                    background: COLORS.bgCard, border: `1px solid ${COLORS.border}`,
                  }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, letterSpacing: '0.05em', marginBottom: 8 }}>
                      SPLIT SUMMARY
                    </div>
                    {groupEntries.map(([key, g]) => (
                      <div key={key} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '6px 0', borderBottom: `1px solid ${COLORS.border}`,
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Icon name={key === '__overhead__' ? 'rocket' : 'leaf'} size={14}
                            color={key === '__overhead__' ? COLORS.gold : COLORS.green} />
                          <span style={{ fontSize: 13, color: COLORS.text }}>{g.label}</span>
                          <span style={{ fontSize: 11, color: COLORS.textMuted }}>({g.count} items)</span>
                        </div>
                        <span style={{ fontSize: 14, fontWeight: 700, color: COLORS.green }}>{fmt(g.total)}</span>
                      </div>
                    ))}
                    {allSameJob && (
                      <div style={{ fontSize: 12, color: COLORS.textDim, marginTop: 8, fontStyle: 'italic' }}>
                        All items assigned to same job — will update the receipt instead of splitting.
                      </div>
                    )}
                  </div>
                );
              })()}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
                <Button variant="ghost" onClick={() => setSplitReceiptData(null)}>Cancel</Button>
                <Button icon="check" onClick={handleSplitSave} disabled={splitReceiptData.items.length === 0}>
                  {(() => {
                    const jobIds = new Set(splitReceiptData.items.map(it => it.jobId || '__oh__'));
                    if (splitReceiptData.tax > 0) jobIds.add(splitReceiptData.taxJobId || '__oh__');
                    return jobIds.size <= 1 ? 'Save Assignment' : `Split into ${jobIds.size} Receipts`;
                  })()}
                </Button>
              </div>
            </>
          )}

          {splitReceiptData.items.length === 0 && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
              <Button variant="ghost" onClick={() => setSplitReceiptData(null)}>Close</Button>
            </div>
          )}
        </Modal>
      )}

      {/* DELETE CONFIRMATION MODAL */}
      {confirmDelete && (
        <Modal title="Delete Receipt" onClose={() => setConfirmDelete(null)}>
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%', background: `${COLORS.danger}18`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 16px', border: `2px solid ${COLORS.danger}`,
            }}>
              <Icon name="trash" size={28} color={COLORS.danger} />
            </div>
            <div style={{ fontSize: 16, fontWeight: 600, color: COLORS.text, marginBottom: 8 }}>
              Delete this receipt?
            </div>
            <div style={{ fontSize: 14, color: COLORS.textDim, marginBottom: 4 }}>
              {confirmDelete.vendor} — {fmt(confirmDelete.amount)}
            </div>
            <div style={{ fontSize: 13, color: COLORS.textMuted, marginBottom: 20 }}>
              {fmtShort(confirmDelete.date)} • {confirmDelete.description}
            </div>
            {confirmDelete.billed && (
              <div style={{
                padding: '10px 14px', background: `${COLORS.danger}18`, borderRadius: 8,
                border: `1px solid ${COLORS.danger}30`, fontSize: 13, color: COLORS.danger,
                marginBottom: 16, textAlign: 'left',
              }}>
                Warning: This receipt has been billed on an invoice. Deleting it won't change the invoice.
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'center', gap: 10 }}>
              <Button variant="ghost" onClick={() => setConfirmDelete(null)}>Cancel</Button>
              <Button variant="danger" icon="trash" onClick={handleDelete}>Delete Receipt</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ============================================================
// LABOR JOURNAL PAGE — fire-and-forget batch save
// ============================================================
function LaborJournalPage({ timeEntries, jobs, displayName, userId, showToast }) {
  const [showBatch, setShowBatch] = useState(false);
  const emptyRow = () => ({ id: Math.random().toString(36).slice(2), date: '', jobId: '', hours: '', task: '' });
  const [batchRows, setBatchRows] = useState(() => Array.from({ length: 5 }, emptyRow));

  const activeJobs = jobs.filter(j => j.status === 'active');
  const totalHours = timeEntries.reduce((s, t) => s + (t.hours || 0), 0);
  const thisWeekHours = timeEntries.filter(t => t.date >= weekStartISO()).reduce((s, t) => s + (t.hours || 0), 0);

  const updateRow = (id, field, value) => setBatchRows(rows => rows.map(r => r.id === id ? { ...r, [field]: value } : r));
  const removeRow = (id) => setBatchRows(rows => rows.filter(r => r.id !== id));
  const addRow = () => setBatchRows(rows => [...rows, emptyRow()]);

  const filledRows = batchRows.filter(r => r.date && r.jobId && r.hours && r.task);
  const batchTotal = filledRows.reduce((s, r) => s + (parseFloat(r.hours) || 0), 0);

  function handleBatchSave() {
    if (filledRows.length === 0) return;
    // Fire and forget — close immediately
    createTimeEntriesBatch(filledRows, displayName);
    showToast(`${filledRows.length} entries saved!`);
    setBatchRows(Array.from({ length: 5 }, emptyRow));
    setShowBatch(false);
  }

  const grouped = useMemo(() => {
    const map = {};
    [...timeEntries].sort((a, b) => (b.date || '').localeCompare(a.date || '')).forEach(t => {
      const d = t.date || 'Unknown';
      if (!map[d]) map[d] = [];
      map[d].push(t);
    });
    return map;
  }, [timeEntries]);

  return (
    <div>
      <PageHeader title="Labor Journal" subtitle={`${totalHours} total hours`} currentUser={displayName}
        actions={<Button icon="plus" onClick={() => setShowBatch(true)}>Batch Entry</Button>} />

      <GlowCard glow style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 13, color: COLORS.textDim }}>This Week</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: COLORS.green }}>{thisWeekHours}h</div>
          </div>
          <div style={{ display: 'flex', gap: 24 }}>
            {activeJobs.slice(0, 3).map(j => {
              const hrs = timeEntries.filter(t => t.jobId === j.id).reduce((s, t) => s + (t.hours || 0), 0);
              return (
                <div key={j.id} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 12, color: COLORS.textMuted }}>{j.clientName.split(' ')[0]}</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: COLORS.text }}>{hrs}h</div>
                </div>
              );
            })}
          </div>
        </div>
      </GlowCard>

      {Object.entries(grouped).map(([date, entries]) => {
        const dayTotal = entries.reduce((s, t) => s + (t.hours || 0), 0);
        return (
          <div key={date} style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.textDim }}>{fmtDate(date)}</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.green }}>{dayTotal}h</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {entries.map(t => {
                const job = jobs.find(j => j.id === t.jobId);
                return (
                  <GlowCard key={t.id}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 500, color: COLORS.text }}>{t.task}</div>
                        <div style={{ fontSize: 12, color: COLORS.textMuted }}>{job?.clientName || 'Unknown'} • {t.createdBy}</div>
                      </div>
                      <div style={{
                        fontSize: 16, fontWeight: 700, color: COLORS.green, background: COLORS.greenGlow,
                        padding: '4px 12px', borderRadius: 8, border: `1px solid ${COLORS.border}`,
                      }}>{t.hours}h</div>
                    </div>
                  </GlowCard>
                );
              })}
            </div>
          </div>
        );
      })}
      {timeEntries.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40, color: COLORS.textMuted }}>
          No time entries yet. Click "Batch Entry" to log your week.
        </div>
      )}

      {showBatch && (
        <Modal title="Batch Time Entry" onClose={() => setShowBatch(false)} wide>
          <div style={{ fontSize: 14, color: COLORS.textDim, marginBottom: 4 }}>
            Enter your week from the journal — one row per day/job.
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px',
            background: COLORS.greenGlow, borderRadius: 8, border: `1px solid ${COLORS.border}`, marginTop: 10, marginBottom: 16,
          }}>
            <Icon name="clock" size={16} color={COLORS.green} />
            <span style={{ fontSize: 14, fontWeight: 600, color: COLORS.green }}>
              {filledRows.length} entries • {batchTotal}h total
            </span>
          </div>

          <div style={{
            display: 'grid', gridTemplateColumns: '110px 1fr 70px 1fr auto',
            gap: 8, padding: '8px 0', borderBottom: `1px solid ${COLORS.border}`, marginBottom: 8,
          }}>
            {['DATE','JOB','HOURS','TASK',''].map((h,i) => (
              <div key={i} style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, letterSpacing: '0.05em' }}>{h}</div>
            ))}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 400, overflowY: 'auto' }}>
            {batchRows.map(row => {
              const isFilled = row.date && row.jobId && row.hours && row.task;
              const inputStyle = (extra) => ({
                background: COLORS.bgInput, border: `1px solid ${COLORS.border}`, borderRadius: 6,
                padding: '7px 8px', color: COLORS.text, fontSize: 13, fontFamily: 'inherit',
                outline: 'none', width: '100%', ...extra,
              });
              return (
                <div key={row.id} style={{
                  display: 'grid', gridTemplateColumns: '110px 1fr 70px 1fr auto',
                  gap: 8, alignItems: 'center', borderLeft: `3px solid ${isFilled ? COLORS.green : 'transparent'}`, paddingLeft: 8,
                }}>
                  <input type="date" value={row.date} onChange={e => updateRow(row.id, 'date', e.target.value)} style={inputStyle()} />
                  <select value={row.jobId} onChange={e => updateRow(row.id, 'jobId', e.target.value)}
                    style={inputStyle({ cursor: 'pointer', color: row.jobId ? COLORS.text : COLORS.textMuted })}>
                    <option value="">Job...</option>
                    {activeJobs.map(j => <option key={j.id} value={j.id}>{j.clientName}</option>)}
                  </select>
                  <input type="number" value={row.hours} onChange={e => updateRow(row.id, 'hours', e.target.value)}
                    placeholder="hrs" step="0.5" min="0" max="24" style={inputStyle({ textAlign: 'center' })} />
                  <input type="text" value={row.task} onChange={e => updateRow(row.id, 'task', e.target.value)}
                    placeholder="What did you work on?" style={inputStyle()} />
                  <button onClick={() => removeRow(row.id)} disabled={batchRows.length <= 1}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, opacity: batchRows.length <= 1 ? 0.2 : 0.5 }}>
                    <Icon name="x" size={16} color={COLORS.textMuted} />
                  </button>
                </div>
              );
            })}
          </div>

          <button onClick={addRow}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              width: '100%', padding: '10px', marginTop: 10,
              background: 'transparent', border: `1px dashed ${COLORS.border}`, borderRadius: 8,
              color: COLORS.textDim, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = COLORS.greenDim; e.currentTarget.style.color = COLORS.green; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = COLORS.border; e.currentTarget.style.color = COLORS.textDim; }}>
            <Icon name="plus" size={14} color="currentColor" /> Add another row
          </button>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20, paddingTop: 16, borderTop: `1px solid ${COLORS.border}` }}>
            <div style={{ fontSize: 13, color: COLORS.textMuted }}>Tab through fields for speed.</div>
            <div style={{ display: 'flex', gap: 10 }}>
              <Button variant="ghost" onClick={() => setShowBatch(false)}>Cancel</Button>
              <Button icon="check" onClick={handleBatchSave} disabled={filledRows.length === 0}>
                Save {filledRows.length} {filledRows.length === 1 ? 'Entry' : 'Entries'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ============================================================
// INVOICES PAGE — full generation flow
// ============================================================
function InvoicesPage({ invoices, jobs, receipts, timeEntries, setPage, displayName, userId, showToast }) {
  const [showPreview, setShowPreview] = useState(null);
  const [showGenerate, setShowGenerate] = useState(false);
  const [genStep, setGenStep] = useState('selectJob'); // selectJob → review → done
  const [selectedJobId, setSelectedJobId] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState(todayISO());

  // Computed data for selected job
  const selectedJob = jobs.find(j => j.id === selectedJobId);
  const unbilledReceipts = receipts.filter(r => r.jobId === selectedJobId && !r.billed && r.status !== 'deleted' && r.status !== 'split');
  const unbilledTime = timeEntries.filter(t => t.jobId === selectedJobId && !t.billed);

  const laborHours = unbilledTime.reduce((s, t) => s + (t.hours || 0), 0);
  const laborRate = selectedJob?.hourlyRate || 0;
  const laborTotal = parseFloat((laborHours * laborRate).toFixed(2));

  const materialsSubtotal = unbilledReceipts.filter(r => !r.isSubcontractor).reduce((s, r) => s + (r.amount || 0), 0);
  const materialsMarkup = parseFloat((materialsSubtotal * 0.10).toFixed(2));
  const materialsTotal = parseFloat((materialsSubtotal + materialsMarkup).toFixed(2));

  const subcontractorsSubtotal = unbilledReceipts.filter(r => r.isSubcontractor).reduce((s, r) => s + (r.amount || 0), 0);
  const subcontractorsMarkup = parseFloat((subcontractorsSubtotal * 0.10).toFixed(2));
  const subcontractorsTotal = parseFloat((subcontractorsSubtotal + subcontractorsMarkup).toFixed(2));

  const grandTotal = parseFloat((laborTotal + materialsTotal + subcontractorsTotal).toFixed(2));

  function startGenerate() {
    setShowGenerate(true);
    setGenStep('selectJob');
    setSelectedJobId('');
    setPeriodStart('');
    setPeriodEnd(todayISO());
  }

  function handleSelectJob() {
    if (!selectedJobId) return;
    // Auto-detect period from earliest unbilled item
    const allDates = [
      ...unbilledReceipts.map(r => r.date),
      ...unbilledTime.map(t => t.date),
    ].filter(Boolean).sort();
    if (allDates.length > 0) {
      setPeriodStart(allDates[0]);
      setPeriodEnd(allDates[allDates.length - 1]);
    }
    setGenStep('review');
  }

  function handleCreateInvoice() {
    if (grandTotal <= 0) return;
    const year = new Date().getFullYear();
    const existingThisYear = invoices.filter(i => i.invoiceYear === year).length;
    const invoiceSeq = existingThisYear + 1;
    const invoiceNumber = `${year}-${String(invoiceSeq).padStart(3, '0')}`;

    const invoiceData = {
      invoiceNumber,
      invoiceSeq,
      invoiceYear: year,
      jobId: selectedJobId,
      clientName: selectedJob.clientName,
      clientAddress: selectedJob.address || '',
      clientEmail: selectedJob.email || '',
      periodStart,
      periodEnd,
      laborHours,
      laborRate,
      laborTotal,
      materialsSubtotal,
      materialsMarkup,
      materialsTotal,
      subcontractorsSubtotal,
      subcontractorsMarkup,
      subcontractorsTotal,
      grandTotal,
    };

    const receiptIds = unbilledReceipts.map(r => r.id);
    const timeEntryIds = unbilledTime.map(t => t.id);

    createInvoice(invoiceData, receiptIds, timeEntryIds, displayName);
    showToast(`Invoice #${invoiceNumber} created!`);
    setShowGenerate(false);
  }

  // Shared invoice preview renderer
  function renderInvoicePreview(inv) {
    return (
      <div style={{ background: '#fff', borderRadius: 8, padding: 40, color: '#1a1a1a', fontFamily: "'SF Mono', 'Fira Code', monospace" }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 32, paddingBottom: 20, borderBottom: '2px solid #7c3aed' }}>
          <div>
            <div style={{ fontSize: 12, letterSpacing: '0.15em', color: '#7c3aed', fontWeight: 700 }}>SATELLITE GARDENS</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#7c3aed' }}>INVOICE</div>
            <div style={{ fontSize: 14, color: '#666' }}>#{inv.invoiceNumber}</div>
          </div>
        </div>
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#999', letterSpacing: '0.1em', marginBottom: 6 }}>BILL TO</div>
          <div style={{ fontSize: 15, fontWeight: 600 }}>{inv.clientName}</div>
          {inv.clientAddress && <div style={{ fontSize: 12, color: '#666' }}>{inv.clientAddress}</div>}
          <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>{fmtShort(inv.periodStart)}</div>
          <div style={{ fontSize: 12, color: '#666' }}>{fmtShort(inv.periodEnd)}</div>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 24 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
              {['DESCRIPTION','DETAILS','AMOUNT'].map((h,i) => (
                <th key={h} style={{ textAlign: i === 0 ? 'left' : 'right', padding: '8px 0', fontSize: 11, fontWeight: 700, color: '#999' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {inv.laborTotal > 0 && (
              <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '12px 0', fontSize: 14 }}>Labor</td>
                <td style={{ padding: '12px 0', fontSize: 13, color: '#666', textAlign: 'right' }}>{inv.laborHours}h × {fmt(inv.laborRate)}</td>
                <td style={{ padding: '12px 0', fontSize: 14, fontWeight: 600, textAlign: 'right' }}>{fmt(inv.laborTotal)}</td>
              </tr>
            )}
            {inv.materialsTotal > 0 && (
              <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '12px 0', fontSize: 14 }}>Materials</td>
                <td style={{ padding: '12px 0', fontSize: 13, color: '#666', textAlign: 'right' }}>{fmt(inv.materialsSubtotal)} + 10%</td>
                <td style={{ padding: '12px 0', fontSize: 14, fontWeight: 600, textAlign: 'right' }}>{fmt(inv.materialsTotal)}</td>
              </tr>
            )}
            {inv.subcontractorsTotal > 0 && (
              <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '12px 0', fontSize: 14 }}>Subcontractors</td>
                <td style={{ padding: '12px 0', fontSize: 13, color: '#666', textAlign: 'right' }}>{fmt(inv.subcontractorsSubtotal)} + 10%</td>
                <td style={{ padding: '12px 0', fontSize: 14, fontWeight: 600, textAlign: 'right' }}>{fmt(inv.subcontractorsTotal)}</td>
              </tr>
            )}
          </tbody>
        </table>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{ padding: '14px 24px', background: '#7c3aed', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 20 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>TOTAL DUE</span>
            <span style={{ fontSize: 24, fontWeight: 800, color: '#fff' }}>{fmt(inv.grandTotal)}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Invoices" subtitle={`${invoices.length} invoices`} currentUser={displayName}
        actions={<Button icon="plus" onClick={startGenerate}>Generate Invoice</Button>} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 24 }}>
        {[
          { label: 'Paid', value: fmt(invoices.filter(i=>i.status==='paid').reduce((s,i)=>s+(i.grandTotal||0),0)), color: COLORS.green },
          { label: 'Outstanding', value: fmt(invoices.filter(i=>i.status==='sent').reduce((s,i)=>s+(i.grandTotal||0),0)), color: COLORS.warning },
          { label: 'Draft', value: fmt(invoices.filter(i=>i.status==='draft').reduce((s,i)=>s+(i.grandTotal||0),0)), color: COLORS.textDim },
        ].map((s, i) => (
          <GlowCard key={i}>
            <div style={{ fontSize: 13, color: COLORS.textMuted }}>{s.label}</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: s.color, marginTop: 4 }}>{s.value}</div>
          </GlowCard>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {invoices.map(inv => (
          <GlowCard key={inv.id} onClick={() => setShowPreview(inv)}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 10, background: COLORS.greenGlow,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${COLORS.border}`,
                }}>
                  <Icon name="fileText" size={20} color={COLORS.green} />
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: COLORS.text }}>
                    #{inv.invoiceNumber} — {inv.clientName}
                  </div>
                  <div style={{ fontSize: 13, color: COLORS.textMuted }}>
                    {fmtShort(inv.periodStart)} – {fmtShort(inv.periodEnd)}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: COLORS.text }}>{fmt(inv.grandTotal)}</div>
                <StatusBadge status={inv.status} />
              </div>
            </div>
          </GlowCard>
        ))}
        {invoices.length === 0 && (
          <div style={{ textAlign: 'center', padding: 40, color: COLORS.textMuted }}>No invoices yet — click "Generate Invoice" to create your first one.</div>
        )}
      </div>

      {/* PREVIEW EXISTING INVOICE */}
      {showPreview && (
        <Modal title={`Invoice #${showPreview.invoiceNumber}`} onClose={() => setShowPreview(null)} wide>
          {renderInvoicePreview(showPreview)}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              {showPreview.status === 'draft' && (
                <Button icon="send" variant="gold" onClick={() => {
                  updateInvoiceStatus(showPreview.id, 'sent');
                  showToast('Invoice marked as sent');
                  setShowPreview(null);
                }}>Mark as Sent</Button>
              )}
              {showPreview.status === 'sent' && (
                <Button icon="check" onClick={() => {
                  updateInvoiceStatus(showPreview.id, 'paid', { paidDate: todayISO() });
                  showToast('Invoice marked as paid!');
                  setShowPreview(null);
                }}>Mark as Paid</Button>
              )}
            </div>
            <Button icon="download" variant="secondary" onClick={async () => {
              try {
                await generateInvoicePDF(showPreview);
                showToast('PDF downloaded!');
              } catch (err) {
                console.error('PDF generation error:', err);
                showToast('Failed to generate PDF');
              }
            }}>Download PDF</Button>
          </div>
        </Modal>
      )}

      {/* GENERATE NEW INVOICE */}
      {showGenerate && (
        <Modal title="Generate Invoice" onClose={() => setShowGenerate(false)} wide>
          {genStep === 'selectJob' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ fontSize: 14, color: COLORS.textDim }}>Select a job to invoice. Only unbilled receipts and time entries will be included.</div>

              <Select label="Job" value={selectedJobId} onChange={setSelectedJobId}
                options={jobs.filter(j => j.status === 'active' || j.status === 'completed').map(j => {
                  const ub = receipts.filter(r => r.jobId === j.id && !r.billed && r.status !== 'deleted' && r.status !== 'split').length;
                  const ut = timeEntries.filter(t => t.jobId === j.id && !t.billed).length;
                  return { value: j.id, label: `${j.clientName} (${ub} receipts, ${ut} time entries unbilled)` };
                })}
                placeholder="Select job..." />

              {selectedJobId && (
                <div style={{
                  padding: '12px 16px', background: COLORS.greenGlow, borderRadius: 8,
                  border: `1px solid ${COLORS.border}`,
                }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.green, marginBottom: 6 }}>
                    {selectedJob?.clientName}
                  </div>
                  <div style={{ display: 'flex', gap: 20, fontSize: 13, color: COLORS.textDim }}>
                    <span>{unbilledReceipts.length} unbilled receipts</span>
                    <span>{unbilledTime.length} unbilled time entries ({laborHours}h)</span>
                  </div>
                  {unbilledReceipts.length === 0 && unbilledTime.length === 0 && (
                    <div style={{ fontSize: 13, color: COLORS.warning, marginTop: 8 }}>No unbilled items for this job.</div>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
                <Button variant="ghost" onClick={() => setShowGenerate(false)}>Cancel</Button>
                <Button icon="chevronRight" onClick={handleSelectJob}
                  disabled={!selectedJobId || (unbilledReceipts.length === 0 && unbilledTime.length === 0)}>
                  Review Items
                </Button>
              </div>
            </div>
          )}

          {genStep === 'review' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Input label="Period Start" value={periodStart} onChange={setPeriodStart} type="date" />
                <Input label="Period End" value={periodEnd} onChange={setPeriodEnd} type="date" />
              </div>

              {/* Labor summary */}
              {unbilledTime.length > 0 && (
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.textMuted, letterSpacing: '0.05em', marginBottom: 8 }}>LABOR</div>
                  {unbilledTime.map(t => (
                    <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${COLORS.border}` }}>
                      <div>
                        <span style={{ fontSize: 13, color: COLORS.text }}>{t.task}</span>
                        <span style={{ fontSize: 12, color: COLORS.textMuted, marginLeft: 8 }}>{fmtShort(t.date)}</span>
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.green }}>{t.hours}h</span>
                    </div>
                  ))}
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: 14, fontWeight: 600 }}>
                    <span style={{ color: COLORS.textDim }}>{laborHours}h × {fmt(laborRate)}</span>
                    <span style={{ color: COLORS.green }}>{fmt(laborTotal)}</span>
                  </div>
                </div>
              )}

              {/* Materials summary */}
              {unbilledReceipts.filter(r => !r.isSubcontractor).length > 0 && (
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.textMuted, letterSpacing: '0.05em', marginBottom: 8 }}>MATERIALS</div>
                  {unbilledReceipts.filter(r => !r.isSubcontractor).map(r => (
                    <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${COLORS.border}` }}>
                      <div>
                        <span style={{ fontSize: 13, color: COLORS.text }}>{r.vendor}</span>
                        <span style={{ fontSize: 12, color: COLORS.textMuted, marginLeft: 8 }}>{r.description}</span>
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{fmt(r.amount)}</span>
                    </div>
                  ))}
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: 14, fontWeight: 600 }}>
                    <span style={{ color: COLORS.textDim }}>{fmt(materialsSubtotal)} + 10% markup</span>
                    <span style={{ color: COLORS.green }}>{fmt(materialsTotal)}</span>
                  </div>
                </div>
              )}

              {/* Grand total */}
              <div style={{
                padding: '16px 20px', background: COLORS.greenGlow, borderRadius: 10,
                border: `1px solid ${COLORS.greenDim}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <span style={{ fontSize: 16, fontWeight: 600, color: COLORS.text }}>Invoice Total</span>
                <span style={{ fontSize: 28, fontWeight: 700, color: COLORS.green }}>{fmt(grandTotal)}</span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                <Button variant="ghost" icon="chevronLeft" onClick={() => setGenStep('selectJob')}>Back</Button>
                <div style={{ display: 'flex', gap: 10 }}>
                  <Button variant="ghost" onClick={() => setShowGenerate(false)}>Cancel</Button>
                  <Button icon="check" onClick={handleCreateInvoice} disabled={grandTotal <= 0}>
                    Create Invoice
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

// ============================================================
// TAX PREP PAGE
// ============================================================
function TaxPrepPage({ receipts, invoices, displayName }) {
  const [activeTab, setActiveTab] = useState('summary');
  const activeReceipts = receipts.filter(r => r.status !== 'deleted' && r.status !== 'split');
  const revenue = invoices.filter(i => i.status === 'paid').reduce((s, i) => s + (i.grandTotal || 0), 0);
  const categories = {};
  activeReceipts.forEach(r => {
    const cat = r.taxCategory || 'Uncategorized';
    categories[cat] = (categories[cat] || 0) + (r.amount || 0);
  });
  const totalExpenses = Object.values(categories).reduce((s, v) => s + v, 0);

  const tabs = ['summary', 'categories', 'vehicle', 'homeOffice', 'export'];

  return (
    <div>
      <PageHeader title="Tax Prep" subtitle="2025 Tax Year" currentUser={displayName}
        actions={<Button icon="download" variant="secondary">Export All</Button>} />

      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: `1px solid ${COLORS.border}` }}>
        {tabs.map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            style={{
              padding: '10px 18px', background: 'transparent', border: 'none',
              borderBottom: `2px solid ${activeTab === tab ? COLORS.green : 'transparent'}`,
              color: activeTab === tab ? COLORS.green : COLORS.textDim,
              cursor: 'pointer', fontSize: 14, fontWeight: 500, fontFamily: 'inherit', marginBottom: -1,
              textTransform: 'capitalize',
            }}>{tab === 'homeOffice' ? 'Home Office' : tab}</button>
        ))}
      </div>

      {activeTab === 'summary' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 24 }}>
            <GlowCard glow>
              <div style={{ fontSize: 13, color: COLORS.textDim }}>Gross Revenue</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: COLORS.green, marginTop: 4 }}>{fmt(revenue)}</div>
            </GlowCard>
            <GlowCard>
              <div style={{ fontSize: 13, color: COLORS.textDim }}>Total Expenses</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: COLORS.danger, marginTop: 4 }}>{fmt(totalExpenses)}</div>
            </GlowCard>
            <GlowCard>
              <div style={{ fontSize: 13, color: COLORS.textDim }}>Net Profit (est.)</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: COLORS.gold, marginTop: 4 }}>{fmt(revenue - totalExpenses)}</div>
            </GlowCard>
          </div>
          <GlowCard>
            <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600, color: COLORS.text }}>Expense Breakdown</h3>
            {Object.entries(categories).sort((a, b) => b[1] - a[1]).map(([cat, amount]) => {
              const pct = totalExpenses > 0 ? (amount / totalExpenses * 100) : 0;
              return (
                <div key={cat} style={{ marginBottom: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: 14, color: COLORS.text }}>{cat}</span>
                    <span style={{ fontSize: 14, fontWeight: 600, color: COLORS.text }}>{fmt(amount)}</span>
                  </div>
                  <div style={{ height: 6, background: COLORS.bgInput, borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: `linear-gradient(90deg, ${COLORS.greenDark}, ${COLORS.green})`, borderRadius: 3 }} />
                  </div>
                </div>
              );
            })}
            {Object.keys(categories).length === 0 && (
              <div style={{ textAlign: 'center', padding: 20, color: COLORS.textMuted }}>No expenses logged yet</div>
            )}
          </GlowCard>
        </div>
      )}

      {activeTab === 'categories' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {Object.entries(categories).sort((a, b) => b[1] - a[1]).map(([cat, amount]) => (
            <GlowCard key={cat}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: COLORS.text }}>{cat}</div>
                  <div style={{ fontSize: 13, color: COLORS.textMuted }}>{activeReceipts.filter(r => r.taxCategory === cat).length} receipts</div>
                </div>
                <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.green }}>{fmt(amount)}</div>
              </div>
            </GlowCard>
          ))}
        </div>
      )}

      {activeTab === 'vehicle' && (
        <GlowCard>
          <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600, color: COLORS.text }}>Vehicle Depreciation — Sprinter Van</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
            <Input label="Purchase Price" value="45000" onChange={() => {}} prefix="$" type="number" />
            <Input label="Business Use %" value="90" onChange={() => {}} type="number" />
          </div>
          <div style={{ padding: 16, background: COLORS.greenGlow, borderRadius: 10, border: `1px solid ${COLORS.border}` }}>
            <div style={{ fontSize: 13, color: COLORS.textDim }}>2025 Depreciation Deduction (Year 2 MACRS)</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: COLORS.green, marginTop: 4 }}>$8,100.00</div>
            <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>$45,000 x 20% x 90%</div>
          </div>
        </GlowCard>
      )}

      {activeTab === 'homeOffice' && (
        <GlowCard>
          <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600, color: COLORS.text }}>Home Office Deduction</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
            <Input label="Total Home Sq Ft" value="1200" onChange={() => {}} type="number" />
            <Input label="Office Sq Ft" value="150" onChange={() => {}} type="number" />
            <Input label="Monthly Rent/Mortgage" value="1800" onChange={() => {}} prefix="$" type="number" />
            <Input label="Monthly Utilities" value="250" onChange={() => {}} prefix="$" type="number" />
          </div>
          <div style={{ padding: 16, background: COLORS.greenGlow, borderRadius: 10, border: `1px solid ${COLORS.border}` }}>
            <div style={{ fontSize: 13, color: COLORS.textDim }}>Business Use: 12.5% (150/1200 sq ft)</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: COLORS.green, marginTop: 4 }}>$3,075.00/year</div>
          </div>
        </GlowCard>
      )}

      {activeTab === 'export' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <GlowCard>
            <div style={{ textAlign: 'center', padding: '24px 16px' }}>
              <Icon name="download" size={36} color={COLORS.green} style={{ display: 'block', margin: '0 auto 12px' }} />
              <div style={{ fontSize: 16, fontWeight: 600, color: COLORS.text, marginBottom: 6 }}>CSV Export</div>
              <div style={{ fontSize: 13, color: COLORS.textMuted, marginBottom: 16 }}>QuickBooks-ready format</div>
              <Button icon="download">Download CSV</Button>
            </div>
          </GlowCard>
          <GlowCard>
            <div style={{ textAlign: 'center', padding: '24px 16px' }}>
              <Icon name="fileText" size={36} color={COLORS.purple} style={{ display: 'block', margin: '0 auto 12px' }} />
              <div style={{ fontSize: 16, fontWeight: 600, color: COLORS.text, marginBottom: 6 }}>Tax Summary PDF</div>
              <div style={{ fontSize: 13, color: COLORS.textMuted, marginBottom: 16 }}>Formatted report with all deductions</div>
              <Button icon="download" variant="secondary">Download PDF</Button>
            </div>
          </GlowCard>
        </div>
      )}
    </div>
  );
}

// ============================================================
// MAIN APP SHELL
// ============================================================
function AppShell() {
  const { currentUser, userProfile, displayName, logout, loading } = useAuth();
  const [page, setPage] = useState('dashboard');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  const [jobs, setJobs] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [invoices, setInvoices] = useState([]);

  const [toast, setToast] = useState(null);
  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  useEffect(() => {
    if (!currentUser) return;

    const unsubs = [
      subscribeToJobs(setJobs),
      subscribeToReceipts(setReceipts),
      subscribeToTimeEntries(setTimeEntries),
      subscribeToInvoices(setInvoices),
    ];

    seedTaxCategories(TAX_CATEGORIES).catch(console.warn);
    initGoogleDrive().catch(console.warn);

    return () => unsubs.forEach(u => u());
  }, [currentUser]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: COLORS.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spinner size={48} label="Connecting to Ground Control..." />
      </div>
    );
  }

  if (!currentUser) return <LoginScreen />;

  const userId = currentUser.uid;
  const pageProps = { jobs, receipts, timeEntries, invoices, setPage, setSelectedJob, displayName, userId, showToast };

  const renderPage = () => {
    switch (page) {
      case 'dashboard': return <DashboardPage {...pageProps} />;
      case 'jobs': return <JobsPage {...pageProps} />;
      case 'jobDetail': return selectedJob ? (
        <div>
          <button onClick={() => setPage('jobs')} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, color: COLORS.textDim, fontFamily: 'inherit', fontSize: 14, marginBottom: 8 }}>
            <Icon name="chevronLeft" size={16} color={COLORS.textDim} /> Jobs
          </button>
          <PageHeader title={selectedJob.clientName} subtitle={selectedJob.address} currentUser={displayName} />
          <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
            <StatusBadge status={selectedJob.status} />
            <Badge color={COLORS.text}>{fmt(selectedJob.hourlyRate)}/hr</Badge>
          </div>
          {(() => {
            const t = computeJobTotals(selectedJob, receipts, timeEntries, invoices);
            return (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginBottom: 28 }}>
                <GlowCard><Stat label="Billed" value={fmt(t.billedTotal)} icon="checkCircle" /></GlowCard>
                <GlowCard><Stat label="Unbilled Materials" value={fmt(t.unbilledMaterials)} icon="receipt" /></GlowCard>
                <GlowCard><Stat label="Unbilled Labor" value={fmt(t.unbilledLabor)} icon="clock" /></GlowCard>
                <GlowCard><Stat label="Total Hours" value={`${t.totalHours}h`} icon="clock" /></GlowCard>
              </div>
            );
          })()}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <div>
              <h3 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 600, color: COLORS.text }}>Receipts</h3>
              {receipts.filter(r => r.jobId === selectedJob.id && r.status !== 'deleted' && r.status !== 'split').slice(0, 8).map(r => (
                <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: `1px solid ${COLORS.border}` }}>
                  <div>
                    <div style={{ fontSize: 14, color: COLORS.text }}>{r.vendor}</div>
                    <div style={{ fontSize: 12, color: COLORS.textMuted }}>{r.description} • {fmtShort(r.date)}</div>
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text }}>{fmt(r.amount)}</div>
                </div>
              ))}
              {receipts.filter(r => r.jobId === selectedJob.id && r.status !== 'deleted' && r.status !== 'split').length === 0 && (
                <div style={{ padding: 20, color: COLORS.textMuted, fontSize: 13 }}>No receipts for this job yet</div>
              )}
            </div>
            <div>
              <h3 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 600, color: COLORS.text }}>Time Entries</h3>
              {timeEntries.filter(t => t.jobId === selectedJob.id).slice(0, 8).map(t => (
                <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: `1px solid ${COLORS.border}` }}>
                  <div>
                    <div style={{ fontSize: 14, color: COLORS.text }}>{t.task}</div>
                    <div style={{ fontSize: 12, color: COLORS.textMuted }}>{fmtShort(t.date)}</div>
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.green }}>{t.hours}h</div>
                </div>
              ))}
              {timeEntries.filter(t => t.jobId === selectedJob.id).length === 0 && (
                <div style={{ padding: 20, color: COLORS.textMuted, fontSize: 13 }}>No time entries for this job yet</div>
              )}
            </div>
          </div>
        </div>
      ) : null;
      case 'upload': return <ReceiptUploadPage {...pageProps} />;
      case 'receipts': return <ReceiptsPage {...pageProps} />;
      case 'time': return <LaborJournalPage {...pageProps} />;
      case 'invoices': return <InvoicesPage {...pageProps} />;
      case 'tax': return <TaxPrepPage {...pageProps} />;
      default: return <DashboardPage {...pageProps} />;
    }
  };

  return (
    <div style={{
      display: 'flex', minHeight: '100vh', background: COLORS.bg,
      fontFamily: "'SF Mono', 'Fira Code', 'JetBrains Mono', ui-monospace, monospace",
      color: COLORS.text,
    }}>
      <Sidebar currentPage={page} setPage={setPage} collapsed={sidebarCollapsed}
        setCollapsed={setSidebarCollapsed} onLogout={logout} />
      <div style={{
        flex: 1, padding: '28px 32px', overflowY: 'auto', maxHeight: '100vh',
        background: `radial-gradient(ellipse at top left, ${COLORS.greenGlow} 0%, transparent 50%), ${COLORS.bg}`,
      }}>
        {renderPage()}
      </div>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppShell />
    </AuthProvider>
  );
}
